import React, { useState } from 'react';
import { Bill } from '../models/Bill';
import { format, parseISO } from 'date-fns';

interface InvoicesProps {
    bills: Bill[];
}

const Invoices: React.FC<InvoicesProps> = ({ bills }) => {
    const [selectedMonth, setSelectedMonth] = useState<string>('');
    const [showDetails, setShowDetails] = useState<{ [key: number]: boolean }>({});

    // Group bills by month
    const groupBillsByMonth = () => {
        const monthlyBills = new Map<string, Bill[]>();
        
        bills.forEach(bill => {
            try {
                // Ensure endDate is a valid Date object
                const endDate = bill.endDate instanceof Date ? bill.endDate : new Date(bill.endDate);
                
                // Use the end date to determine which month the bill belongs to
                const monthKey = format(endDate, 'yyyy-MM'); // Use yyyy-MM format for month grouping
                
                if (!monthlyBills.has(monthKey)) {
                    monthlyBills.set(monthKey, []);
                }
                
                monthlyBills.get(monthKey)!.push(bill);
            } catch (error) {
                console.error('Error processing bill date:', bill, error);
            }
        });
        
        // Sort by month (newest first)
        return Array.from(monthlyBills.entries())
            .sort((a, b) => b[0].localeCompare(a[0]));
    };

    // Filter bills by selected month if any
    const getFilteredBills = () => {
        if (!selectedMonth) {
            return groupBillsByMonth();
        }
        
        const filteredBills = bills.filter(bill => {
            try {
                // Ensure endDate is a valid Date object
                const endDate = bill.endDate instanceof Date ? bill.endDate : new Date(bill.endDate);
                const monthKey = format(endDate, 'yyyy-MM'); // Use yyyy-MM format for consistency
                return monthKey === selectedMonth;
            } catch (error) {
                console.error('Error filtering bill by date:', bill, error);
                return false;
            }
        });
        
        return [[selectedMonth, filteredBills]] as [string, Bill[]][];
    };

    // Toggle details for a specific bill
    const toggleDetails = (billIndex: number) => {
        setShowDetails(prev => ({
            ...prev,
            [billIndex]: !prev[billIndex]
        }));
    };

    // Get available months for the dropdown
    const getAvailableMonths = () => {
        const months = Array.from(new Set(
            bills.map(bill => {
                try {
                    // Ensure endDate is a valid Date object
                    const endDate = bill.endDate instanceof Date ? bill.endDate : new Date(bill.endDate);
                    return format(endDate, 'yyyy-MM'); // Use yyyy-MM format for months
                } catch (error) {
                    console.error('Error getting available months for bill:', bill, error);
                    return 'invalid';
                }
            }).filter(month => month !== 'invalid') // Filter out invalid dates
        )).sort().reverse();
        
        return months;
    };

    // Calculate total costs for a month
    const getMonthlyTotals = (monthBills: Bill[]) => {
        return monthBills.reduce((totals, bill) => ({
            totalAmount: totals.totalAmount + bill.getTotalAmount(),
            totalUse: totals.totalUse + bill.use,
            totalCost: totals.totalCost + bill.cost,
            totalTndCost: totals.totalTndCost + bill.tndCost,
            totalGenCost: totals.totalGenCost + bill.genCost
        }), {
            totalAmount: 0,
            totalUse: 0,
            totalCost: 0,
            totalTndCost: 0,
            totalGenCost: 0
        });
    };

    const filteredBills = getFilteredBills();

    return (
        <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
            {/* Header */}
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '30px',
                paddingBottom: '15px',
                borderBottom: '2px solid #e0e0e0'
            }}>
                <h1 style={{ 
                    color: '#2c3e50', 
                    margin: 0,
                    fontSize: '28px',
                    fontWeight: 'bold'
                }}>
                    📄 Energy Invoices
                </h1>
                
                {/* Month Filter */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <label style={{ fontWeight: 'bold', color: '#555' }}>
                        Filter by Month:
                    </label>
                    <select
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        style={{
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: '2px solid #ddd',
                            fontSize: '14px',
                            backgroundColor: 'white',
                            minWidth: '150px'
                        }}
                    >
                        <option value="">All Months</option>
                        {getAvailableMonths().map(month => (
                            <option key={month} value={month}>
                                {format(parseISO(month + '-01'), 'MMMM yyyy')}
                            </option>
                        ))}
                    </select>
                    
                    {selectedMonth && (
                        <button
                            onClick={() => setSelectedMonth('')}
                            style={{
                                padding: '8px 12px',
                                backgroundColor: '#e74c3c',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '12px'
                            }}
                            title="Clear filter"
                        >
                            ✕ Clear
                        </button>
                    )}
                </div>
            </div>

            {/* Bills Display */}
            {filteredBills.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    padding: '40px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    color: '#666'
                }}>
                    <h3>No invoices found</h3>
                    <p>There are no bills available for the selected period.</p>
                </div>
            ) : (
                <div>
                    {filteredBills.map(([monthKey, monthBills]) => {
                        const monthTotals = getMonthlyTotals(monthBills);
                        
                        return (
                            <div key={monthKey} style={{ marginBottom: '40px' }}>
                                {/* Month Header */}
                                <div style={{
                                    backgroundColor: '#3498db',
                                    color: 'white',
                                    padding: '20px',
                                    borderRadius: '8px 8px 0 0',
                                    marginBottom: '0'
                                }}>
                                    <div style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center'
                                    }}>
                                        <h2 style={{ margin: 0, fontSize: '24px' }}>
                                            📅 {format(parseISO(monthKey + '-01'), 'MMMM yyyy')}
                                        </h2>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                                                Total: ${monthTotals.totalAmount.toFixed(2)}
                                            </div>
                                            <div style={{ fontSize: '14px', opacity: 0.9 }}>
                                                {monthBills.length} invoice{monthBills.length !== 1 ? 's' : ''}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Month Summary */}
                                <div style={{
                                    backgroundColor: '#ecf0f1',
                                    padding: '15px 20px',
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                    gap: '15px',
                                    fontSize: '14px'
                                }}>
                                    <div>
                                        <strong>Total Usage:</strong> {monthTotals.totalUse.toFixed(1)} {monthBills[0]?.useUnit || 'kWh'}
                                    </div>
                                    <div>
                                        <strong>Energy Cost:</strong> ${monthTotals.totalCost.toFixed(2)}
                                    </div>
                                    <div>
                                        <strong>T&D Cost:</strong> ${monthTotals.totalTndCost.toFixed(2)}
                                    </div>
                                    <div>
                                        <strong>Generation Cost:</strong> ${monthTotals.totalGenCost.toFixed(2)}
                                    </div>
                                </div>

                                {/* Individual Bills */}
                                <div style={{
                                    backgroundColor: 'white',
                                    border: '1px solid #ddd',
                                    borderTop: 'none',
                                    borderRadius: '0 0 8px 8px'
                                }}>
                                    {monthBills.map((bill, billIndex) => (
                                        <div key={billIndex} style={{
                                            borderBottom: billIndex < monthBills.length - 1 ? '1px solid #eee' : 'none',
                                            padding: '20px'
                                        }}>
                                            {/* Bill Summary */}
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginBottom: '15px'
                                            }}>
                                                <div>
                                                    <h3 style={{ 
                                                        margin: 0, 
                                                        color: '#2c3e50',
                                                        fontSize: '18px' 
                                                    }}>
                                                        Invoice #{billIndex + 1}
                                                    </h3>
                                                    <p style={{ 
                                                        margin: '5px 0', 
                                                        color: '#666',
                                                        fontSize: '14px' 
                                                    }}>
                                                        {(() => {
                                                            try {
                                                                const startDate = bill.startDate instanceof Date ? bill.startDate : new Date(bill.startDate);
                                                                const endDate = bill.endDate instanceof Date ? bill.endDate : new Date(bill.endDate);
                                                                return `${format(startDate, 'MMM dd')} - ${format(endDate, 'MMM dd, yyyy')} (${bill.getBillPeriodInDays()} days)`;
                                                            } catch (error) {
                                                                console.error('Error formatting bill dates:', error);
                                                                return 'Date formatting error';
                                                            }
                                                        })()}
                                                    </p>
                                                </div>
                                                
                                                <div style={{ textAlign: 'right' }}>
                                                    <div style={{ 
                                                        fontSize: '20px', 
                                                        fontWeight: 'bold',
                                                        color: '#27ae60'
                                                    }}>
                                                        ${bill.getTotalAmount().toFixed(2)}
                                                    </div>
                                                    <div style={{ 
                                                        fontSize: '12px', 
                                                        color: '#666' 
                                                    }}>
                                                        ${bill.getAverageDailyCost().toFixed(2)}/day avg
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Quick Info Grid */}
                                            <div style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                                                gap: '15px',
                                                marginBottom: '15px',
                                                padding: '15px',
                                                backgroundColor: '#f8f9fa',
                                                borderRadius: '6px',
                                                fontSize: '14px'
                                            }}>
                                                <div>
                                                    <div style={{ color: '#666', fontSize: '12px' }}>Usage</div>
                                                    <div style={{ fontWeight: 'bold' }}>
                                                        {bill.use} {bill.useUnit}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div style={{ color: '#666', fontSize: '12px' }}>Demand</div>
                                                    <div style={{ fontWeight: 'bold' }}>
                                                        {bill.demand} {bill.demandUnit}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div style={{ color: '#666', fontSize: '12px' }}>Peak Demand</div>
                                                    <div style={{ fontWeight: 'bold' }}>
                                                        {bill.peakDemand} {bill.demandUnit}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div style={{ color: '#666', fontSize: '12px' }}>Tariff</div>
                                                    <div style={{ fontWeight: 'bold' }}>
                                                        {bill.tariff}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Show/Hide Details Button */}
                                            <button
                                                onClick={() => toggleDetails(billIndex)}
                                                style={{
                                                    padding: '8px 16px',
                                                    backgroundColor: showDetails[billIndex] ? '#e74c3c' : '#3498db',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {showDetails[billIndex] ? '▲ Hide Details' : '▼ Show Details'}
                                            </button>

                                            {/* Detailed Breakdown */}
                                            {showDetails[billIndex] && (
                                                <div style={{
                                                    marginTop: '20px',
                                                    padding: '20px',
                                                    backgroundColor: '#f8f9fa',
                                                    borderRadius: '6px',
                                                    border: '1px solid #e0e0e0'
                                                }}>
                                                    <h4 style={{ 
                                                        margin: '0 0 15px 0', 
                                                        color: '#2c3e50' 
                                                    }}>
                                                        💰 Cost Breakdown
                                                    </h4>
                                                    
                                                    <div style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: '1fr auto',
                                                        gap: '10px',
                                                        fontSize: '14px'
                                                    }}>
                                                        <div>Energy Cost:</div>
                                                        <div style={{ fontWeight: 'bold' }}>
                                                            ${bill.cost.toFixed(2)}
                                                        </div>
                                                        
                                                        <div>Transmission & Distribution Cost:</div>
                                                        <div style={{ fontWeight: 'bold' }}>
                                                            ${bill.tndCost.toFixed(2)}
                                                        </div>
                                                        
                                                        <div>Generation Cost:</div>
                                                        <div style={{ fontWeight: 'bold' }}>
                                                            ${bill.genCost.toFixed(2)}
                                                        </div>
                                                        
                                                        <div style={{ 
                                                            borderTop: '2px solid #ddd', 
                                                            paddingTop: '8px',
                                                            fontWeight: 'bold',
                                                            fontSize: '16px'
                                                        }}>
                                                            Total Amount:
                                                        </div>
                                                        <div style={{ 
                                                            borderTop: '2px solid #ddd', 
                                                            paddingTop: '8px',
                                                            fontWeight: 'bold',
                                                            fontSize: '16px',
                                                            color: '#27ae60'
                                                        }}>
                                                            ${bill.getTotalAmount().toFixed(2)}
                                                        </div>
                                                    </div>                                    {/* Bill Variances */}
                                    {bill.billVariances && (
                                        <div style={{ marginTop: '20px' }}>
                                            <h4 style={{ 
                                                margin: '0 0 15px 0', 
                                                color: '#2c3e50' 
                                            }}>
                                                📊 Bill Variances
                                            </h4>
                                            <div style={{
                                                padding: '10px',
                                                backgroundColor: 'white',
                                                borderRadius: '4px',
                                                marginBottom: '10px',
                                                border: '1px solid #ddd',
                                                fontSize: '12px'
                                            }}>
                                                <div><strong>Status:</strong> {bill.billVariances.statusMessage}</div>
                                                <div><strong>Status Code:</strong> {bill.billVariances.statusCode}</div>
                                                {bill.billVariances.baselineBill && (
                                                    <div style={{ marginTop: '5px' }}>
                                                        <div><strong>Baseline Period:</strong> {bill.billVariances.baselineBill.start} to {bill.billVariances.baselineBill.end}</div>
                                                        <div><strong>Baseline Cost:</strong> ${bill.billVariances.baselineBill.totalCost?.toFixed(2)}</div>
                                                        <div><strong>Baseline Usage:</strong> {bill.billVariances.baselineBill.totalUse?.toFixed(1)} {bill.useUnit}</div>
                                                    </div>
                                                )}
                                                {bill.billVariances.variances && bill.billVariances.variances.length > 0 && (
                                                    <div style={{ marginTop: '10px' }}>
                                                        <div><strong>Variances:</strong></div>
                                                        {bill.billVariances.variances.map((variance, vIndex) => (
                                                            <div key={vIndex} style={{
                                                                marginLeft: '15px',
                                                                marginTop: '5px',
                                                                padding: '5px',
                                                                backgroundColor: '#f8f9fa',
                                                                borderRadius: '3px'
                                                            }}>
                                                                <div><strong>Category:</strong> {variance.category}</div>
                                                                {variance.absoluteVariance !== undefined && (
                                                                    <div><strong>Absolute Variance:</strong> {variance.absoluteVariance}</div>
                                                                )}
                                                                {variance.percentageVariance !== undefined && (
                                                                    <div><strong>Percentage Variance:</strong> {variance.percentageVariance}%</div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default Invoices;
